import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { RegulationDraftView, SourceLinkButton } from './RegulationViews';
import { SupersedeModal } from './RegulationModals';
import SourceTextViewer from './SourceTextViewer';
import { useResource } from '../../hooks/useResource';
import { useApiRequest } from '../../hooks/useApiRequest';
import { userMessageForError } from '../../utils/apiError';
import {
    breakGlassReasonValid,
    buildApproveBody,
    describeApproveError,
    describeEnrichError,
    documentTypeLabel,
    rejectReasonValid,
} from '../../utils/regulatoryModel';

const { Text } = Typography;

/**
 * One pending AI draft. Approve is the human-approval event of Commandment IV: it echoes the `draftHash`
 * of exactly the draft on screen, so a draft that changed under the reviewer is refused (409 DRAFT_CHANGED).
 * Separation of duties (A.5.3): the admin who triggered a draft cannot approve it; break-glass is offered
 * only after the server refuses AND says it is available on this deployment.
 */
/** The document a draft says it replaces: shown with its title/reference so the reviewer can check it, never auto-applied. */
const SupersessionMatch = ({ row, onOpenItem, onChanged }) => {
    const { request } = useApiRequest();
    const candidateId = row.draft?.supersessionCandidate?.candidateId || null;
    const [open, setOpen] = useState(false);
    const old = useResource(async () => (await request('GET', `/regulatory-content/${candidateId}`)).data.item, [request, candidateId], { enabled: !!candidateId });
    if (!candidateId) return null;
    if (old.status === 'error') return <Alert type="warning" style={{ marginTop: 8 }} message="The matched document could not be loaded." />;
    if (!old.data) return null;
    const o = old.data;
    return (
        <div style={{ marginTop: 8 }} data-testid="supersession-match">
            <Text strong>Matched document: </Text>
            <Text>{o.title}{o.officialDocumentNumber ? ` (${o.officialDocumentNumber})` : ''}</Text>
            {o.supersededById && <Tag color="red" style={{ marginLeft: 8 }}>Already superseded</Tag>}
            <Space style={{ marginLeft: 8 }}>
                {onOpenItem && <Button onClick={() => onOpenItem(o.id)}>Open document</Button>}
                {!o.supersededById && <Button onClick={() => setOpen(true)}>Mark old document superseded</Button>}
            </Space>
            {open && (
                <SupersedeModal
                    item={o}
                    initialReplacement={{ id: row.itemId, title: row.item?.title || '', officialDocumentNumber: row.item?.officialDocumentNumber }}
                    onClose={() => setOpen(false)}
                    onDone={async () => { setOpen(false); await onChanged(); }}
                />
            )}
        </div>
    );
};

const DraftReviewCard = ({ row, breakGlassAvailable, onChanged, onOpenItem, topicTitles }) => {
    const { request } = useApiRequest();
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [separation, setSeparation] = useState(false);
    const [bgRefused, setBgRefused] = useState(false);
    const [regenerable, setRegenerable] = useState(!!(row.stale || row.itemChanged));
    const [breakGlass, setBreakGlass] = useState(false);
    const [breakGlassReason, setBreakGlassReason] = useState('');
    const [rejectOpen, setRejectOpen] = useState(false);
    const [reason, setReason] = useState('');

    // A reloaded, different draft must be re-read: the acknowledgement never carries over to a new hash.
    useEffect(() => { setChecked(false); }, [row.draftHash]);
    useEffect(() => { if (row.stale || row.itemChanged) setRegenerable(true); }, [row.stale, row.itemChanged]);

    const approve = async () => {
        setBusy(true);
        setError(null);
        try {
            const { data } = await request('PATCH', `/regulatory-content/enrichments/${row.id}/approve`, {
                body: buildApproveBody(row.draftHash, { breakGlass, breakGlassReason }),
            });
            message.success(data?.version != null ? `Approved and published as version ${data.version}.` : 'Approved and published.');
            await onChanged();
        } catch (err) {
            const view = describeApproveError(err);
            setError(view.message);
            if (view.separation) setSeparation(true);
            if (view.breakGlassAvailable === false) setBgRefused(true);
            if (view.itemChanged) setRegenerable(true);
            if (view.reload) await onChanged();
        } finally {
            setBusy(false);
        }
    };

    // The old draft must be rejected before a new one can be created (one pending draft per item).
    const draftAgain = async () => {
        setBusy(true);
        setError(null);
        try {
            await request('PATCH', `/regulatory-content/enrichments/${row.id}/reject`, { body: { reason: 'Regenerated: the item changed after this draft was created.' } });
            await request('POST', `/regulatory-content/enrich/${row.itemId}`);
            message.success('A new draft was created from the current record.');
            await onChanged();
        } catch (err) {
            setError(describeEnrichError(err));
            await onChanged();
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        setBusy(true);
        setError(null);
        try {
            await request('PATCH', `/regulatory-content/enrichments/${row.id}/reject`, { body: { reason: reason.trim() } });
            setRejectOpen(false);
            message.success('Draft rejected. It is kept with your reason.');
            await onChanged();
        } catch (err) {
            setRejectOpen(false);
            setError(userMessageForError(err));
            if (err?.status === 409 || err?.status === 404) await onChanged();
        } finally {
            setBusy(false);
        }
    };

    const item = row.item || {};
    const needsBreakGlassReason = breakGlass && !breakGlassReasonValid(breakGlassReason);
    const approveDisabled = busy || row.stale || !checked || needsBreakGlassReason;

    return (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginBottom: 16 }} data-testid="review-card">
            <Space style={{ marginBottom: 8 }}>
                <Tag>{item.authorityName}</Tag>
                <Tag>{documentTypeLabel(item.documentType)}</Tag>
                <Text strong>{item.title}</Text>
            </Space>
            <div><Text type="secondary">{item.officialDocumentNumber || 'No reference number'}</Text></div>
            <div style={{ margin: '8px 0' }}><SourceLinkButton url={item.sourceUrl} /></div>
            <RegulationDraftView draft={row.draft} itServicesRelevance={row.itServicesRelevance} stale={row.stale} triggeredBy={row.triggeredBy} topicTitles={topicTitles} />
            {row.itemChanged && !row.stale && <Alert type="warning" style={{ marginTop: 8 }} data-testid="draft-item-changed" message="The item changed after this draft was created; regenerate the draft." />}

            <SupersessionMatch row={row} onOpenItem={onOpenItem} onChanged={onChanged} />
            <SourceTextViewer itemId={row.itemId} />

            {error && <Alert type="error" style={{ marginTop: 12 }} message={error} data-testid="review-error" />}
            {regenerable && <div style={{ marginTop: 8 }}><Button onClick={draftAgain} disabled={busy}>Draft again</Button></div>}
            {separation && (
                breakGlassAvailable && !bgRefused ? (
                    <div style={{ marginTop: 8 }} data-testid="break-glass-approve">
                        <Checkbox checked={breakGlass} onChange={(e) => setBreakGlass(e.target.checked)}>
                            Approve under break-glass. Allowed only because this deployment has a single platform admin; it is recorded in the audit log.
                        </Checkbox>
                        {breakGlass && (
                            <Input.TextArea
                                aria-label="Break-glass reason"
                                rows={2}
                                placeholder="Why can a second admin not approve? (at least 15 characters)"
                                value={breakGlassReason}
                                onChange={(e) => setBreakGlassReason(e.target.value)}
                            />
                        )}
                    </div>
                ) : (
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>Ask a second platform admin to approve this draft.</Text>
                )
            )}

            <div style={{ marginTop: 12 }}>
                <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
                    I have checked the obligations, deadlines and quotes against the official source.
                </Checkbox>
            </div>
            <Space style={{ marginTop: 8 }}>
                <Button onClick={approve} disabled={approveDisabled}>Approve and publish</Button>
                <Button onClick={() => setRejectOpen(true)} disabled={busy}>Reject</Button>
            </Space>

            <Modal
                open={rejectOpen}
                title="Reject this draft"
                okText="Reject draft"
                onOk={reject}
                onCancel={() => setRejectOpen(false)}
                okButtonProps={{ disabled: !rejectReasonValid(reason) || busy }}
                confirmLoading={busy}
            >
                <Input.TextArea aria-label="Rejection reason" rows={3} placeholder="Why is this draft not acceptable? (5 to 1000 characters)" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Modal>
        </div>
    );
};

/** The pending-draft review queue (GET /regulatory-content/enrichments?pending=true). */
const RegulatoryReviewQueue = ({ queue, total, offset, pageSize, hasMore, nextOffset, onPage, breakGlassAvailable, onChanged, onOpenItem, topicTitles }) => {
    if (queue.status === 'loading' && !queue.data) return <Spin />;
    if (queue.status === 'error') {
        return <Alert type="error" message={userMessageForError(queue.error)} action={<Button onClick={queue.reload}>Retry</Button>} />;
    }
    const rows = queue.data || [];
    if (rows.length === 0 && offset > 0 && total > 0) return <Button onClick={() => onPage(0)}>Back to the first drafts</Button>;
    if (rows.length === 0) return <Empty description="No AI drafts are waiting for review." />;
    return (
        <div>
            {rows.map((row) => (
                <DraftReviewCard key={row.id} row={row} breakGlassAvailable={breakGlassAvailable} onChanged={onChanged} onOpenItem={onOpenItem} topicTitles={topicTitles} />
            ))}
            {total > pageSize && (
                <Space data-testid="queue-pager">
                    <Button disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - pageSize))}>Previous drafts</Button>
                    <Text type="secondary">{offset + 1}-{offset + rows.length} of {total}</Text>
                    <Button disabled={hasMore === undefined ? offset + rows.length >= total : !hasMore} onClick={() => onPage(nextOffset ?? offset + pageSize)}>Next drafts</Button>
                </Space>
            )}
        </div>
    );
};

export default RegulatoryReviewQueue;
